import { StyleSheet, View, Text } from "react-native";

import { AttachmentChip, AttachmentChipProps } from "../attachmentChip/AttachmentChip";
import { colors, radii, spacing, typographyStyles } from "../../../../styles/design-system";
import CustomMarkdown from "../../../common/general/CustomMarkdown/CustomMarkdown";

export interface AITutorMessageBubbleProps {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  taskAttachmentChips?: AttachmentChipProps[];
  isArchived: boolean;
  isFullWidth?: boolean;
}

export const AITutorMessageBubble = ({
  content,
  role,
  timestamp,
  taskAttachmentChips = [],
  isArchived,
  isFullWidth = false,
}: AITutorMessageBubbleProps) => {
  const messageStyles = [styles.messageBubble, role === "user" ? styles.userMessage : styles.assistantMessage];
  const bubbleStyles = [
    styles.bubble,
    role === "user" ? styles.userBubble : styles.assistantBubble,
    role === "user" && isArchived && styles.userArchivedBubble,
  ];
  const messageWrapperStyles = [
    styles.messageWrapper,
    role === "user" ? styles.userMessageWrapper : styles.assistantMessageWrapper,
    isFullWidth && styles.messageWrapperFullWidth,
  ];
  const textStyles = [
    styles.message,
    typographyStyles.textBase,
    role === "user" ? styles.userText : styles.assistantText,
    role === "user" && isArchived && styles.userArchivedText,
  ];

  const formattedTimestamp =
    role === "user"
      ? new Date(timestamp).toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : undefined;

  return (
    <View style={messageStyles}>
      <View style={messageWrapperStyles}>
        {(taskAttachmentChips ?? []).map((attachment, index) => (
          <AttachmentChip key={index} {...attachment} removeChip={isArchived ? undefined : attachment?.removeChip} />
        ))}
        <View style={bubbleStyles}>
          <CustomMarkdown
            style={{
              body: textStyles,
              ...(role === "user" && {
                paragraph: { marginTop: 0, marginBottom: 0 },
              }),
            }}
            content={content}
          />
        </View>
        <View style={styles.actionBar}>
          <Text style={styles.timestamp}>{formattedTimestamp}</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  messageBubble: {
    alignSelf: "stretch",
    display: "flex",
    alignItems: "flex-start",
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  userMessage: {
    flexDirection: "row-reverse",
  },
  assistantMessage: {
    flexDirection: "row",
  },
  bubble: {
    borderRadius: radii.lg,
  },
  userBubble: {
    padding: spacing.sm,
    borderTopRightRadius: radii.sm,
    backgroundColor: colors.brand[500],
  },
  userArchivedBubble: {
    backgroundColor: colors.grey[400],
  },
  userArchivedText: {
    color: colors.white,
  },
  assistantBubble: {
    backgroundColor: "transparent",
  },
  messageWrapper: {
    display: "flex",
    maxWidth: "90%",
  },
  messageWrapperFullWidth: {
    maxWidth: "100%",
    width: "100%",
  },
  userMessageWrapper: {
    alignItems: "flex-end",
  },
  assistantMessageWrapper: {
    alignItems: "flex-start",
  },
  message: {
    textWrap: "wrap",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    // @ts-expect-error - works for web
    display: "block",
  },
  userText: {
    textAlign: "right",
    color: colors.white,
  },
  assistantText: {
    textAlign: "left",
    color: colors.grey["700"],
    // @ts-expect-error - works for web
    display: "block",
  },
  actionBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    marginTop: spacing.xxs,
    gap: spacing.xs,
    color: colors.grey[500],
    ...typographyStyles.textXs,
  },
  timestamp: {
    color: colors.grey[500],
    ...typographyStyles.textXs,
  },
});
